import {variationsToArray} from '@findhotel/sapi'
import {createSelector} from '@reduxjs/toolkit'

import {getQueryStringObject} from '../../_web/utils/url'
import {brands} from '../../brand/config'
import {getIsProductionAppEnv} from '../../utils/environment'
import {collectBovioVariationsParam} from '../bovio/collectBovioVariationsParam'
import {collectSapiVariationsParam} from '../sapi'
import {ExperimentsState} from '../types'

type DataFile = {
  featureFlags: Array<{key: string}>
}

/**
 * Selector that returns the experiments datafile.
 * @param state [ExperimentsState]
 * @returns the datafile containing the experiments from optimizely
 */
export const getDataFile = createSelector(
  [(state: ExperimentsState) => state.experiments?.dataFile as DataFile],
  dataFile => dataFile
)

/**
 * Selector that returns the SAPI variations header.
 * @param state [ExperimentsState]
 * @returns the SAPI variations header
 */
export const getSapiVariationsHeader = createSelector(
  [getDataFile],
  dataFile => {
    const sapiVariations = collectSapiVariationsParam(
      dataFile?.featureFlags?.map(({key}) => key)
    )
    return {
      'x-sapi-variations': variationsToArray(sapiVariations).join(',')
    }
  }
)

/**
 * Selector that returns the BoVio API key header.
 * @param state - Redux state
 * @returns BoVIO bovio API key
 */
// Opticks caches decisions; caching by datafile here would retain the fallback after identification.
export const getBovioApiKey = (state: ExperimentsState) => {
  const dataFile = getDataFile(state)
  const isProdEnv = getIsProductionAppEnv(process.env.NEXT_PUBLIC_ENV)

  const forcedApiKey =
    !isProdEnv && typeof window !== 'undefined'
      ? getQueryStringObject().forceBovioApiKey
      : undefined

  if (forcedApiKey) {
    return String(forcedApiKey)
  }

  const dataFileExperiments = dataFile?.featureFlags?.map(({key}) => key)
  const toggleEnvSuffix = isProdEnv ? '' : '_test'
  const allKeys = [
    brands.vio['bovioProdApiKey'],
    ...collectBovioVariationsParam(dataFileExperiments)
  ].map(key => `${key}${toggleEnvSuffix}`)

  return allKeys.join(',')
}
